import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
// apiClient, not axiosClient: every route this panel touches lives in hrms
// (/api/tally, /api/credentials). axiosClient points at the auth-service, where
// none of them exist — the calls 404 and the panel just looks empty.
import { apiClient } from '../../auth-lib';
import {
  Button, Card, Input, Select, StatusPill, EmptyState, Modal, useToast,
} from '../../design-system';
import AgentInstallHelp from './client/AgentInstallHelp';
import AgentMachinePanel from './agent/AgentMachinePanel';
import TallyPosition from './tally/TallyPosition';
import { useLiveRefresh } from '../../lib/useLiveRefresh';
import '../styles/TallyPanel.css';
import { invalidateTallyNavStatus } from '../../lib/tallyNavStatus';

// The client record's Tally tab: is this client's Tally connected, how fresh are
// the books, and what shape is the client in.
//
// ⚠️ ONE REPORT, DELIBERATELY. This tab used to carry four tabs of its own —
// ledgers, entries, Tally-computed reports and party matching. All four are now
// on the FIRM-level Tally page (/org-admin/tally), because every one of them is
// something a practice does across its whole book rather than one client at a
// time: you browse ledgers when you are looking for something, you read entries
// when you are reconciling, you clear a match queue in one sitting. Reaching
// them meant opening a client first, which is the wrong level for all three.
//
// What is left is the question somebody opens a CLIENT record to ask: what does
// this client hold, what do they owe, and is anything obviously wrong. That is
// TallyPosition, and it is the whole of this tab below the header.
//
// The levels, stated once because conflating them has already cost real damage:
//
//   a Tally COMPANY  →  a client of the firm      (the firm-level /org-admin/tally)
//   a LEDGER inside  →  a customer of THAT client (the firm page's Books tab)
//
// ⚠️ THE CONNECTION CONTROLS STAY HERE even though the books moved. Freshness
// is the one thing that cannot be read anywhere else at the moment it matters:
// somebody reading this client's position has to be able to see it is four days
// old and re-run the sync from where they are standing, not go to another page
// to do it.
//
// ⚠️ FRESHNESS IS THE HEADLINE, NOT A FOOTNOTE. Tally only answers when it is
// running with the company open, so "we could not reach the books last night"
// is an ordinary outcome rather than an exception. Every figure this tab can
// lead to is only as good as its last successful sync, and a preparer who does
// not notice that files a return from stale numbers. So the age of the data is
// the first thing rendered and it is never hidden once it goes bad.

const READY_LABEL = {
  online: 'running now',
  stale: 'not running',
  offline: 'not running',
  never: 'never started',
};

// ⚠️ NO LOCAL IDEA OF "WORKING" LIVES IN THIS FILE. Liveness, capability and
// the machine's self-check are all decided by the server's utils/agentHealth.js
// and arrive on each machine as `liveness`, `tally` and `selfCheck`. A second
// definition here would drift from the one the picker is judged by.

function freshness(conn) {
  if (!conn) return null;
  if (!conn.lastSuccessfulSyncAt) {
    return { variant: 'warning', text: 'Never synced' };
  }
  const h = conn.ageHours;
  if (h < 1) return { variant: 'success', text: 'Synced within the hour' };
  if (h < 24) return { variant: 'success', text: `Synced ${h}h ago` };
  const days = Math.floor(h / 24);
  // Deliberately generous before crying stale: a machine off for a night is
  // ordinary, and an alarm every Monday teaches people to ignore the indicator.
  return {
    variant: conn.stale ? 'danger' : 'warning',
    text: `Synced ${days} day${days === 1 ? '' : 's'} ago`,
  };
}

function TallyPanel({ clientId }) {
  const toast = useToast();
  const revision = useLiveRefresh();

  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  // Connect flow
  const [connectOpen, setConnectOpen] = useState(false);
  const [agents, setAgents] = useState([]);
  const [agentId, setAgentId] = useState('');
  const [companies, setCompanies] = useState([]);
  const [company, setCompany] = useState('');
  const [discovering, setDiscovering] = useState(false);
  const [typedCompany, setTypedCompany] = useState('');
  const [installOpen, setInstallOpen] = useState(false);
  const [installMode, setInstallMode] = useState('install');
  const [machineBusy, setMachineBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      // Only the connection — its freshness is what the header renders, and it
      // is also what TallyPosition needs beside its figures. The position
      // itself is a second call made by that component, so a client's books
      // (tens of thousands of vouchers) are never loaded to draw a header.
      const res = await apiClient.get('/api/tally/connections', { params: { clientId } });
      setConnections(res.data || []);
    } catch {
      // Leave the last known state rather than blanking the tab — a transient
      // 401 during token refresh must not read as "Tally is not connected".
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => { load(); }, [load, revision]);

  const connection = connections[0] || null;
  const fresh = freshness(connection);

  // ── Connect ───────────────────────────────────────────────────────────────

  const openConnect = async () => {
    setConnectOpen(true);
    setCompanies([]);
    setCompany('');
    try {
      const res = await apiClient.get('/api/credentials/agents/mine');
      // The endpoint answers { agents: [...] }, not a bare array — reading it as
      // one leaves the machine picker permanently empty with nothing logged.
      const list = res.data?.agents || [];
      setAgents(list);
      // One machine is the overwhelmingly common case; making the user pick it
      // out of a list of one is a step that exists only because the code found
      // it easier. Its state is shown either way, so this hides nothing.
      if (list.length === 1) setAgentId(list[0].id);
    } catch {
      toast.danger('Could not list the agents on your machines');
    }
  };

  /**
   * Ask a machine what Tally companies it can see.
   *
   * Polled rather than awaited in one request: the agent may be up to a poll
   * interval away, and holding an HTTP request open that long would tie up a
   * connection per setup attempt for no benefit.
   */
  const discover = async () => {
    if (!agentId) return;
    setDiscovering(true);
    setCompanies([]);
    try {
      const { data } = await apiClient.post('/api/tally/discover', { agentInstallId: agentId });
      const deadline = Date.now() + 90000;
      let lastStatus = 'queued';

      // eslint-disable-next-line no-constant-condition
      while (true) {
        // eslint-disable-next-line no-await-in-loop
        await new Promise((r) => setTimeout(r, 2000));
        // eslint-disable-next-line no-await-in-loop
        const res = await apiClient.get(`/api/tally/discover/${data.jobId}`);
        lastStatus = res.data.status;
        if (res.data.status === 'succeeded') {
          setCompanies(res.data.companies || []);
          if (!res.data.companies?.length) {
            toast.warning('Tally answered but reported no companies. Is a company open?');
          }
          break;
        }
        if (res.data.status === 'failed' || res.data.status === 'expired') {
          toast.danger(res.data.error || 'The agent could not reach Tally');
          break;
        }
        if (Date.now() > deadline) {
          // ⚠️ TWO DIFFERENT FAILURES WITH TWO DIFFERENT FIXES, and the job's own
          // status tells them apart. Still 'queued' means nothing ever claimed
          // it — the sync service is not running on that machine, and Tally was
          // never contacted at all, so "is Tally open?" is the wrong question to
          // send someone away with. 'claimed' means the agent took it and Tally
          // itself did not answer.
          toast.danger(
            lastStatus === 'queued'
              ? 'Nothing on that machine picked this up — the Tally sync service is not running there.'
              : 'The agent asked Tally and got no answer. Is Tally open, with the gateway switched on?',
          );
          break;
        }
      }
    } catch (e) {
      toast.danger(e.response?.data?.error || 'Could not start discovery');
    } finally {
      setDiscovering(false);
    }
  };

  // ⚠️ REINSTALLING IS REPAIR, NOT A SECOND MACHINE. `registerAgent` upserts on
  // the machine fingerprint, so a computer that sets itself up again revives the
  // same row — which is why this can be offered as a fix rather than as a way to
  // accumulate dead entries.
  const reinstallMachine = () => {
    setInstallMode('reinstall');
    setConnectOpen(false);
    setInstallOpen(true);
  };

  const forgetMachine = async (agent) => {
    setMachineBusy(true);
    try {
      await apiClient.post(`/api/credentials/agents/${agent.id}/forget`);
      const res = await apiClient.get('/api/credentials/agents/mine');
      const list = res.data?.agents || [];
      setAgents(list);
      // The selection has to go with it, or the picker keeps a machine that is
      // no longer in the list and "List companies" targets a revoked id.
      setAgentId(list.length === 1 ? list[0].id : '');
      setCompanies([]);
      setCompany('');
      toast.success('That computer has been removed. Setting it up again will bring it back.');
    } catch (e) {
      toast.danger(e.response?.data?.error || 'Could not remove that computer');
    } finally {
      setMachineBusy(false);
    }
  };

  // `name` lets the by-name path connect a company that was never discovered,
  // and therefore has no entry in `companies` to read a GUID or books-from date
  // out of. Both are optional on the server; the name is the part that matters.
  const connect = async (name) => {
    const target = name || company;
    if (!agentId || !target) return;
    setBusy(true);
    try {
      const chosen = companies.find((c) => c.name === target);
      await apiClient.post('/api/tally/connections', {
        clientId,
        agentInstallId: agentId,
        companyName: target,
        companyGuid: chosen?.guid || '',
        booksFrom: chosen?.booksFrom || null,
      });
      toast.success('Tally connected. The first sync has started.');
      setConnectOpen(false);
      // A brand-new connection is exactly the change the sidebar dot exists to
      // report, and the cache it reads is invisible from here.
      invalidateTallyNavStatus();
      await load();
    } catch (e) {
      toast.danger(e.response?.data?.error || 'Could not connect');
    } finally {
      setBusy(false);
    }
  };

  // ── Operations ────────────────────────────────────────────────────────────

  const syncNow = async () => {
    setBusy(true);
    try {
      await apiClient.post(`/api/tally/connections/${connection.id}/sync`);
      toast.success('Sync queued. It runs as soon as the agent picks it up.');
      await load();
    } catch (e) {
      toast.danger(e.response?.data?.error || 'Could not start a sync');
    } finally {
      setBusy(false);
    }
  };

  const setStatus = async (status) => {
    setBusy(true);
    try {
      await apiClient.patch(`/api/tally/connections/${connection.id}`, { status });
      // The sidebar's Tally dot is cached across navigations and cannot see
      // this. Without it the dot reports the old connection state until the
      // cache ages out.
      invalidateTallyNavStatus();
      toast.success(status === 'revoked' ? 'Tally connection revoked' : `Connection ${status}`);
      await load();
    } catch (e) {
      toast.danger(e.response?.data?.error || 'Could not update the connection');
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <p className="tally-muted">Loading…</p>;

  // ── Not connected ─────────────────────────────────────────────────────────

  if (!connection) {
    return (
      <>
        <EmptyState
          icon="📗"
          title="Tally is not connected for this client"
          description="Connect the machine that runs their Tally to mirror the books here every night."
          action={<Button onClick={openConnect}>Connect Tally</Button>}
        />
        <ConnectModal />
        <InstallHelp />
      </>
    );
  }

  // ── Connected ─────────────────────────────────────────────────────────────

  return (
    <>
      <Card>
        <div className="tally-head">
          <div>
            <h3 className="tally-company">{connection.companyName}</h3>
            <div className="tally-status-row">
              <StatusPill variant={fresh.variant} label={fresh.text} />
              {connection.status !== 'active' && (
                <StatusPill variant="warning" label={connection.status} />
              )}
              {!connection.backfillComplete && (
                <StatusPill variant="info" label="Historical backfill in progress" />
              )}
            </div>
          </div>
          <div className="tally-actions">
            <Button variant="secondary" onClick={syncNow} disabled={busy || connection.status !== 'active'}>
              Sync now
            </Button>
            {connection.status === 'active' ? (
              <Button variant="secondary" onClick={() => setStatus('paused')} disabled={busy}>Pause</Button>
            ) : connection.status === 'paused' ? (
              <Button variant="secondary" onClick={() => setStatus('active')} disabled={busy}>Resume</Button>
            ) : null}
            {connection.status !== 'revoked' && (
              <Button variant="danger" onClick={() => setStatus('revoked')} disabled={busy}>Revoke</Button>
            )}
          </div>
        </div>

        {/* The error is shown verbatim. "Tally is not running" and "no company
            is open" are different problems with different fixes, and collapsing
            them into "sync failed" turns a self-service correction into a
            support call. */}
        {connection.lastError && (
          <p className="tally-error">
            <strong>Last attempt failed:</strong> {connection.lastError}
            {connection.consecutiveFailures > 1 && ` (${connection.consecutiveFailures} times in a row)`}
          </p>
        )}

        {connection.stale && !connection.lastError && (
          <p className="tally-warn">
            These books have not refreshed recently. Anything read from them may be out of date.
          </p>
        )}

        <MachineNote machine={connection.machine} />
      </Card>

      {/* ⚠️ NO TABS. One report, rendered directly. A tab strip with a single
          tab in it is a control that answers a question nobody asked, and it
          would also be a standing invitation to put the ledger and entry views
          back beside it — which is the duplication this change exists to
          remove. Everything else about this client's Tally is on the firm's
          Tally page, and the line below says so rather than leaving somebody to
          wonder where the ledger list went. */}
      <TallyPosition connectionId={connection.id} connection={connection} />

      <Card>
        <div className="tally-head">
          <div>
            <h3 className="tally-section-title">The rest of these books</h3>
            <p className="tally-muted">
              The ledger list, the individual entries, the reports Tally computes
              and the party-matching queue all live on the firm&apos;s Tally
              page, where you can move between companies without opening a
              client record for each one.
            </p>
          </div>
          {/* ⚠️ DEEP-LINKED TO THIS COMPANY, not to the page. Landing on the
              Companies tab and making somebody find the company they were
              already looking at — in a picker that holds every client the firm
              has in Tally — is the errand this button exists to remove. The
              query is read by pages/OrgAdminTally.jsx (`tab`) and
              components/tally/TallyBooks.jsx (`company`).

              ⚠️ A `Link` WEARING THE BUTTON'S CLASSES, not `<Button>`. The
              design system's Button renders a motion.button and takes no `as`
              prop, so handing it a `to` would spread that onto a <button> as a
              stray DOM attribute and produce something that looks like a link
              and navigates nowhere. An anchor is also the honest element here:
              it is a navigation, so it should middle-click into a new tab and
              show its destination on hover. Same treatment as the download
              action in client/AgentInstallHelp.jsx. */}
          <div className="tally-actions">
            <Link
              className="ds-btn ds-btn-primary ds-btn-md"
              to={`/org-admin/tally?tab=books&company=${connection.id}`}
            >
              Open these books →
            </Link>
          </div>
        </div>

        {/* Guarded as a whole, not just its contents: an always-rendered <p>
            carries its margin whether or not it has anything in it, leaving a
            band of empty space under the card on every client with a clean
            match queue — which is most of them. */}
        {connection.pendingMatches > 0 && (
          <p className="tally-warn">
            {`${connection.pendingMatches} ledger${connection.pendingMatches === 1 ? '' : 's'} in these books ${connection.pendingMatches === 1 ? 'is' : 'are'} waiting to be matched to a client.`}
          </p>
        )}
      </Card>

      <ConnectModal />
      <InstallHelp />
    </>
  );

  /**
   * What this client's machine can and cannot do with closed companies.
   *
   * ⚠️ SHOWN ONLY WHEN IT CHANGES WHAT SOMEBODY MUST DO. A machine that opens
   * companies by itself needs no instruction, and a line saying so on every
   * client would be noise that trains people to skip the whole area — including
   * the one case that matters. So: the 'none' verdict, which imposes a standing
   * requirement on the firm, and nothing else.
   *
   * ⚠️ THE WORDS COME FROM THE SERVER. utils/tally/openStrategy.js holds the copy
   * for all five verdicts, so a strategy added later explains itself without a
   * matching frontend deploy — and there is no second, drifting map of what
   * "none" means.
   */
  function MachineNote({ machine }) {
    const [showEvidence, setShowEvidence] = useState(false);
    if (!machine?.syncsOnlyOpenCompanies) return null;

    return (
      <div className="tally-machine-note">
        <p className="tally-machine-head">{machine.headline}</p>
        <p className="tally-machine-detail">{machine.detail}</p>
        {machine.evidence && (
          <>
            <button
              type="button"
              className="tally-linkish"
              onClick={() => setShowEvidence((v) => !v)}
            >
              {showEvidence ? 'Hide what was tested' : 'What was tested?'}
            </button>
            {/* The machine's own words, verbatim. This is a verdict a firm may
                well dislike, and "what did it actually try?" is the first thing
                they will ask — a summary would just move that question. */}
            {showEvidence && <pre className="tally-evidence">{machine.evidence}</pre>}
          </>
        )}
      </div>
    );
  }

  function ConnectModal() {
    return (
      <Modal
        isOpen={connectOpen}
        onClose={() => setConnectOpen(false)}
        title="Connect Tally"
      >
        <p className="tally-muted">
          Tally must be running on the chosen machine with the company open.
          Nothing can be read while it is closed.
        </p>

        {/* An empty picker is the commonest first-run state and used to be a
            dead end: nothing to choose, a disabled button, and no hint that a
            program has to be installed on the machine running Tally. The reader
            is sent to the same self-installing agent the Credentials tab uses
            rather than being left to guess. */}
        {agents.length === 0 ? (
          <>
            <p className="tally-warn">
              No machine has the agent installed yet. It has to run on the
              computer where Tally is open — this one, if that is here.
            </p>
            <Button onClick={() => { setInstallMode('install'); setConnectOpen(false); setInstallOpen(true); }}>
              Install it on this computer
            </Button>
          </>
        ) : (
          <>
            <Select
              label="Machine running Tally"
              value={agentId}
              onChange={(e) => { setAgentId(e.target.value); setCompanies([]); setCompany(''); }}
            >
              <option value="">Choose a machine…</option>
              {agents.map((a) => (
                <option key={a.id} value={a.id}>
                  {`${a.label} (${a.version || 'unknown version'}) — ${READY_LABEL[a.liveness] || a.liveness}`}
                </option>
              ))}
            </Select>

            {/* ⚠️ SHOWN, NOT ENFORCED. The verdict can be a few minutes stale —
                somebody may have opened Tally since the last self-check — so it
                explains and offers the fix while leaving the button live. The
                same rule the attendance geofence follows: classify, never
                permit. */}
            <AgentMachinePanel
              agent={agents.find((a) => a.id === agentId)}
              busy={machineBusy || discovering}
              onReinstall={reinstallMachine}
              onForget={forgetMachine}
            />

            <Button onClick={discover} disabled={!agentId || discovering} loading={discovering}>
              {discovering ? 'Asking Tally…' : 'List companies'}
            </Button>
          </>
        )}

        {/* ⚠️ DISCOVERY ONLY EVER LISTS COMPANIES THAT ARE OPEN — Tally's
            ListOfCompanies reports what is loaded, not what exists on disk. So a
            firm connecting their fourth client had to go and open that client's
            company in Tally first, purely to satisfy a picker. The name is all
            the server ever needed; it is what every request quotes.

            Deliberately secondary to the list: a typed name that does not match
            what Tally calls the company fails silently at sync time, having
            looked like it worked. The list is right when it is available. */}
        {agents.length > 0 && (
          <details className="tally-by-name">
            <summary>The company isn’t open in Tally right now</summary>
            <p className="tally-muted">
              Type its name exactly as Tally shows it. It has to be open when a
              sync runs, but not to set this up.
            </p>
            <Input
              label="Tally company name"
              value={typedCompany}
              onChange={(e) => setTypedCompany(e.target.value)}
              placeholder="Harsh Traders"
            />
            <Button
              variant="secondary"
              onClick={() => connect(typedCompany.trim())}
              disabled={!typedCompany.trim() || busy}
            >
              Connect by name
            </Button>
          </details>
        )}

        {companies.length > 0 && (
          <>
            {/* Chosen by a human, never matched automatically. Tally company
                names and client names disagree constantly, and a wrong binding
                silently mirrors one client's books under another's name. */}
            <Select
              label="Tally company for this client"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
            >
              <option value="">Choose a company…</option>
              {companies.map((c) => (
                <option key={c.name} value={c.name}>{c.name}</option>
              ))}
            </Select>
            <Button onClick={() => connect()} disabled={!company || busy}>
              Connect and start the first sync
            </Button>
          </>
        )}
      </Modal>
    );
  }

  function InstallHelp() {
    if (!installOpen) return null;
    return (
      <AgentInstallHelp
        mode={installMode}
        onClose={() => setInstallOpen(false)}
        // Installing was never the errand — reopen the connect flow, which
        // re-reads the machine list and now has something in it.
        onReady={() => { setInstallOpen(false); openConnect(); }}
      />
    );
  }
}

export default TallyPanel;
